import { _ as _defineProperty } from './defineProperty-e24c82ea.esm.js';
import { F as FileOrBufferOrStringSchema, P as PercentSchema, A as AmountSchema, a as BasisPointsSchema, Q as QuantitySchema, c as QueryAllParamsSchema, g as getRpcUrl, b as CommonNFTInput } from './QueryParams-5f9e3d4a.esm.js';
import { z } from 'zod';
import { PublicKey, Keypair, clusterApiUrl, Connection } from '@solana/web3.js';
import { guestIdentity, amount, SOL, isKeypairSigner, keypairIdentity, isIdentitySigner, walletAdapterIdentity, token, findMetadataPda, toBigNumber, sol, TransactionBuilder, getSignerHistogram, findMasterEditionV2Pda, toNftOriginalEdition, toOriginalEditionAccount, GmaBuilder, toMetadata, toMetadataAccount, toDateTime, resolveClusterFromConnection, Pda, TokenGpaBuilder, Metaplex } from '@metaplex-foundation/js';
import { createCreateMetadataAccountV2Instruction, TokenStandard } from '@metaplex-foundation/mpl-token-metadata';
import { setProvider, AnchorProvider, Program as Program$1 } from '@project-serum/anchor';
import { Buffer as Buffer$1 } from 'buffer/';
import BN from 'bn.js';
import bs58 from 'bs58';
import EventEmitter from 'eventemitter3';
import invariant from 'tiny-invariant';
import nacl from 'tweetnacl';
import { v4 } from 'uuid';
import { isBrowser, ThirdwebStorage, IpfsUploader } from '@thirdweb-dev/storage';
import { getAssociatedTokenAddress, getAccount } from '@solana/spl-token';

/**
 * @internal
 */
const PropertiesInput = z.object({}).catchall(z.unknown());

/**
 * @internal
 */
const OptionalPropertiesInput = z.union([z.array(PropertiesInput), PropertiesInput]).optional();

/**
 * @internal
 */
const RawDateSchema = z.date().transform(i => {
  return Math.floor(i.getTime() / 1000);
});

/**
 * @internal
 */

/**
 * @internal
 */
const AddressSchema = z.union([z.string(), z.instanceof(PublicKey).transform(key => key.toBase58())]);

/**
 * @internal
 */
const LoginOptionsSchema = z.object({
  /**
   * The optional nonce of the login request used to prevent replay attacks
   */
  nonce: z.string().optional(),
  /**
   * The optional time after which the login payload will be invalid
   */
  expirationTime: z.date().optional()
}).optional();

/**
 * @internal
 */
const LoginPayloadDataSchema = z.object({
  /**
   * The domain that the user is attempting to login to
   */
  domain: z.string(),
  /**
   * The public key of the account that is logging in
   */
  address: AddressSchema,
  /**
   * The nonce of the login request used to prevent replay attacks, defaults to a random UUID
   */
  nonce: z.string().default(v4()),
  /**
   * The time after which the login payload will be invalid, defaults to 5 minutes from now
   */
  expiration_time: z.date().transform(d => d.toISOString())
});

/**
 * @internal
 */
z.object({
  /**
   * The payload data used for login
   */
  payload: LoginPayloadDataSchema,
  /**
   * The signature of the login request used for verification
   */
  signature: z.string()
});

/**
 * @internal
 */
const AuthenticationOptionsSchema = z.object({
  /**
   * The date before which the authentication payload is invalid
   */
  invalidBefore: z.date().optional(),
  /**
   * The date after which the authentication payload is invalid
   */
  expirationTime: z.date().optional()
}).optional();

/**
 * @internal
 */
const AuthenticationPayloadDataSchema = z.object({
  /**
   * The address of the wallet issuing the payload
   */
  iss: z.string(),
  /**
   * The address of the wallet requesting to authenticate
   */
  sub: z.string(),
  /**
   * The domain intended to receive the authentication payload
   */
  aud: z.string(),
  /**
   * The date before which the authentication payload is invalid
   */
  exp: RawDateSchema,
  /**
   * The date after which the authentication payload is invalid
   */
  nbf: RawDateSchema,
  /**
   * The date on which the payload was issued
   */
  iat: RawDateSchema,
  /**
   * The unique identifier of the payload
   */
  jti: z.string().default(v4())
});

/**
 * @internal
 */
z.object({
  /**
   * The payload data used for authentication
   */
  payload: AuthenticationPayloadDataSchema,
  /**
   * The signature of the authentication payload used for authentication
   */
  signature: z.string()
});

/**
 * @public
 */

/**
 * Wallet Authenticator
 * @remarks The wallet authenticator enables server-side applications to securely identify the
 * connected wallet address of users on the client-side, and also enables users to authenticate
 * to any backend using just their wallet. It implements the JSON Web Token (JWT) authentication
 * standard.
 *
 * @example
 * ```javascript
 * // We specify the domain of the application to authenticate to
 * const domain = "example.com"
 *
 * // On the client side, we can generate a payload for the connected wallet to login
 * const loginPayload = await sdk.auth.login(domain);
 *
 * // Then on the server side, we can securely verify the connected client-side address
 * const address = sdk.auth.verify(domain, loginPayload);
 *
 * // And we can also generate an authentication token to send to the client
 * const token = sdk.auth.generate(domain, loginPayload);
 *
 * // Finally, the token can be send from the client to the server to make authenticated requests
 * // And the server can use the following function to authenticate a token and verify the associated address
 * const address = sdk.auth.authenticate(domain, token);
 * ```
 * @public
 */
class WalletAuthenticator {
  constructor(wallet) {
    _defineProperty(this, "wallet", void 0);
    this.wallet = wallet;
  }

  /**
   * Login With Connected Wallet
   * @remarks Client-side function that allows the connected wallet to login to a server-side application.
   * Generates a login payload that can be sent to the server-side for verification or authentication.
   *
   * @param domain - The domain of the server-side application to login to
   * @param options - Optional configuration options for the login request
   * @returns Login payload that can be used on the server-side to verify the login request or authenticate
   *
   * @example
   * ```javascript
   * // Add the domain of the application users will login to, this will be used throughout the login process
   * const domain = "example.com";
   * // Generate a signed login payload for the connected wallet to authenticate with
   * const loginPayload = await sdk.auth.login(domain);
   * ```
   */
  async login(domain, options) {
    const parsedOptions = LoginOptionsSchema.parse(options);
    const signerAddress = this.wallet.getAddress();
    const expirationTime = parsedOptions?.expirationTime || new Date(Date.now() + 1000 * 60 * 5);
    const payloadData = LoginPayloadDataSchema.parse({
      domain,
      address: signerAddress,
      nonce: parsedOptions?.nonce,
      expiration_time: expirationTime
    });
    const message = this.generateMessage(payloadData);
    const signature = await this.wallet.sign(message);
    return {
      payload: payloadData,
      signature
    };
  }

  /**
   * Verify Logged In Address
   * @remarks Server-side function to securely verify the address of the logged in client-side wallet
   * by validating the provided client-side login request.
   *
   * @param domain - The domain of the server-side application to verify the login request for
   * @param payload - The login payload to verify
   * @returns The public key of the logged in wallet
   *
   * @example
   * ```javascript
   * const domain = "example.com";
   * const loginPayload = await sdk.auth.login(domain);
   *
   * // Verify the login request
   * const address = sdk.auth.verify(domain, loginPayload);
   * ```
   */
  verify(domain, payload) {
    // Check that the intended domain matches the domain of the payload
    if (payload.payload.domain !== domain) {
      throw new Error(`Expected domain '${domain}' does not match domain on payload '${payload.payload.domain}'`);
    }

    // Check that the payload hasn't expired
    const currentTime = new Date();
    if (currentTime > new Date(payload.payload.expiration_time)) {
      throw new Error(`Login request has expired`);
    }

    // Check that the signing address is the claimed wallet address
    const message = this.generateMessage(payload.payload);
    const isValid = this.wallet.verifySignature(message, payload.signature, payload.payload.address);
    if (!isValid) {
      throw new Error(`Signer address '${payload.payload.address}' did not sign the provided message`);
    }
    return payload.payload.address;
  }

  /**
   * Generate Authentication Token
   * @remarks Server-side function that generates a JWT token from the provided login request that the
   * client-side wallet can use to authenticate to the server-side application.
   *
   * @param domain - The domain of the server-side application to authenticate to
   * @param payload - The login payload to authenticate with
   * @param options - Optional configuration options for the authentication request
   * @returns A authentication token that can be used by the client to make authenticated requests
   *
   * @example
   * ```javascript
   * const domain = "example.com";
   * const loginPayload = await sdk.auth.login(domain);
   *
   * // Generate a JWT token that can be sent to the client-side wallet and used for authentication
   * const token = await sdk.auth.generateAuthToken(domain, loginPayload);
   * ```
   */
  async generateAuthToken(domain, payload, options) {
    if (isBrowser()) {
      throw new Error("Authentication tokens should not be generated in the browser, as they must be signed by a server-side admin wallet.");
    }
    const parsedOptions = AuthenticationOptionsSchema.parse(options);
    const userAddress = this.verify(domain, payload);
    const adminAddress = this.wallet.getAddress();
    const payloadData = AuthenticationPayloadDataSchema.parse({
      iss: adminAddress,
      sub: userAddress,
      aud: domain,
      nbf: parsedOptions?.invalidBefore || new Date(),
      exp: parsedOptions?.expirationTime || new Date(Date.now() + 1000 * 60 * 60 * 5),
      iat: new Date()
    });
    const message = JSON.stringify(payloadData);
    const signature = await this.wallet.sign(message);

    // Header used for JWT token specifying hash algorithm
    const header = {
      // Specify ECDSA with SHA-256 for hashing algorithm
      alg: "ES256",
      typ: "JWT"
    };
    const encodedHeader = Buffer.from(JSON.stringify(header)).toString("base64");
    const encodedData = Buffer.from(JSON.stringify(payloadData)).toString("base64").replace(/=/g, "");
    const encodedSignature = Buffer.from(signature).toString("base64");

    // Generate a JWT token with base64 encoded header, payload, and signature
    const token = `${encodedHeader}.${encodedData}.${encodedSignature}`;
    return token;
  }

  /**
   * Authenticate With Token
   * @remarks Server-side function that authenticates the provided JWT token. This function verifies that
   * the provided authentication token is valid and returns the address of the authenticated wallet.
   *
   * @param domain - The domain of the server-side application doing authentication
   * @param token - The authentication token being used
   * @returns The address of the authenticated wallet
   *
   * @example
   * ```javascript
   * const domain = "example.com";
   * const loginPayload = await sdk.auth.login(domain);
   * const token = await sdk.auth.generateAuthToken(domain, loginPayload);
   *
   * // Authenticate the token and get the address of authenticating users wallet
   * const address = sdk.auth.authenticate(domain, token);
   * ```
   */
  async authenticate(domain, token) {
    if (isBrowser()) {
      throw new Error("Should not authenticate tokens in the browser, as they must be verified by the server-side admin wallet.");
    }
    const encodedPayload = token.split(".")[1];
    const encodedSignature = token.split(".")[2];
    const payload = JSON.parse(Buffer.from(encodedPayload, "base64").toString());
    const signature = Buffer.from(encodedSignature, "base64").toString();

    // Check that the token audience matches the domain
    if (payload.aud !== domain) {
      throw new Error(`Expected token to be for the domain '${domain}', but found token with domain '${payload.aud}'`);
    }

    // Check that the token is past the invalid before time
    const currentTime = Math.floor(new Date().getTime() / 1000);
    if (currentTime < payload.nbf) {
      throw new Error(`This token is invalid before epoch time '${payload.nbf}', current epoch time is '${currentTime}'`);
    }

    // Check that the token hasn't expired
    if (currentTime > payload.exp) {
      throw new Error(`This token expired at epoch time '${payload.exp}', current epoch time is '${currentTime}'`);
    }

    // Check that the connected wallet matches the token issuer
    const connectedAddress = this.wallet.getAddress();
    if (connectedAddress?.toLowerCase() !== payload.iss.toLowerCase()) {
      throw new Error(`Expected the connected wallet address '${connectedAddress}' to match the token issuer address '${payload.iss}'`);
    }

    // Check that the connected wallet signed the token
    const isValid = this.wallet.verifySignature(JSON.stringify(payload), signature, connectedAddress);
    if (!isValid) {
      throw new Error(`The connected wallet address '${connectedAddress}' did not sign the token`);
    }
    return payload.sub;
  }

  /**
   * Generates a SIWS compliant message to sign based on the login payload
   */
  generateMessage(payload) {
    let message = ``;

    // Add the domain and login address for transparency
    message += `${payload.domain} wants you to sign in with your account:\n${payload.address}\n\n`;

    // Prompt user to make sure domain is correct to prevent phishing attacks
    message += `Make sure that the requesting domain above matches the URL of the current website.\n\n`;
    message += `Nonce: ${payload.nonce}\n`;
    message += `Expiration Time: ${payload.expiration_time}\n`;
    return message;
  }
}

/**
 * @internal
 */
function toCurrencyValue(amount) {
  return {
    value: amount.basisPoints.toString(),
    displayValue: toDisplayValue(amount)
  };
}
const toDisplayValue = value => {
  if (value.currency.decimals === 0) {
    return `${value.currency.symbol} ${value.basisPoints.toString()}`;
  }
  const power = new BN(10).pow(new BN(value.currency.decimals));
  const basisPoints = value.basisPoints;
  const {
    div,
    mod
  } = basisPoints.divmod(power);
  const units = `${div.toString()}.${mod.abs().toString(10, value.currency.decimals)}`;
  return `${units}`;
};

/**
 *
 * {@link UserWallet} events that you can subscribe to using `sdk.wallet.events`.
 *
 * @public
 */

/**
 * Handle and view info about the wallet connected to the SDK.
 *
 * @example
 * ```jsx
 * // Connect a wallet to the SDK, pass in a keypair or browser wallet adapter
 * sdk.wallet.connect(signer)
 *
 * // Then you can read data about the connected wallet
 * const address = sdk.wallet.getAddress();
 * ```
 *
 * @public
 */
class UserWallet {
  get network() {
    const url = new URL(this.metaplex.connection.rpcEndpoint);
    // try this first to avoid hitting `custom` network for alchemy urls
    if (url.hostname.includes("devnet")) {
      return "devnet";
    }
    if (url.hostname.includes("mainnet")) {
      return "mainnet-beta";
    }
    return this.metaplex.cluster;
  }
  constructor(metaplex) {
    _defineProperty(this, "signer", void 0);
    _defineProperty(this, "events", new EventEmitter());
    _defineProperty(this, "metaplex", void 0);
    this.metaplex = metaplex;
  }

  /**
   * Connect a signer to the SDK. Can pass in a keypair or browser wallet adapter
   * @param wallet - The signer to connect to the SDK
   *
   * @example
   * ```jsx
   * const signer = Keypair.generate();
   * sdk.wallet.connect(signer);
   * ```
   */
  connect(wallet) {
    this.connectToMetaplex(wallet);
    this.events.emit("connected", wallet);
  }

  /**
   * Disconnect the connect wallet from the SDK
   *
   * @example
   * ```jsx
   * sdk.wallet.disconnect();
   * ```
   */
  disconnect() {
    // TODO implement our own read only identity plugin with our own error messages
    this.metaplex.use(guestIdentity());
    this.events.emit("disconnected");
  }

  /**
   * Return whether a wallet is connected
   */
  isConnected() {
    return this.metaplex.identity().publicKey !== PublicKey.default;
  }

  /**
   * Get the address of the connected wallet
   * @returns the address of the connected wallet
   *
   * @example
   * ```jsx
   * const address = sdk.wallet.getAddress()
   * ```
   */
  getAddress() {
    return this.isConnected() ? this.metaplex.identity().publicKey.toBase58() : undefined;
  }

  /**
   * Get the connected signer
   * @returns the signer
   *
   * @example
   * ```jsx
   * const signer = sdk.wallet.getSigner()
   * ```
   */
  getSigner() {
    return this.metaplex.identity();
  }
  async sign(message) {
    const signer = this.getSigner();
    const encodedMessage = new TextEncoder().encode(message);
    const signedMessage = await signer.signMessage(encodedMessage);
    const signature = bs58.encode(signedMessage);
    return signature;
  }
  verifySignature(message, signature, publicKey) {
    return nacl.sign.detached.verify(new TextEncoder().encode(message), bs58.decode(signature), bs58.decode(publicKey));
  }

  /**
   * Get the native balance of the connected wallet
   * @returns the native balance currency value
   *
   * @example
   * ```jsx
   * const balance = await sdk.wallet.getBalance();
   * console.log(balance.displayValue);
   * ```
   */
  async getBalance() {
    const value = await this.metaplex.connection.getBalance(this.metaplex.identity().publicKey);
    return toCurrencyValue(amount(value, SOL));
  }
  connectToMetaplex(signer) {
    invariant(signer, "Wallet is not connected");
    const plugin = isKeypairSigner(signer) ? keypairIdentity(Keypair.fromSecretKey(signer.secretKey)) : isIdentitySigner(signer) ? walletAdapterIdentity(signer) : undefined;
    invariant(plugin, "Wallet is not compatible with Metaplex");
    this.metaplex.use(plugin);
  }
}

/**
 * @internal
 */
const CommonContractSchema = z.object({
  name: z.string(),
  symbol: z.string().optional(),
  description: z.string().optional(),
  image: FileOrBufferOrStringSchema.optional(),
  external_link: z.string().url().optional()
});

/**
 * @internal
 */
const CommonContractOutputSchema = CommonContractSchema.extend({
  image: z.string().optional()
}).catchall(z.unknown());

/// NFT ///

/**
 * @internal
 */
const CreatorInputSchema = z.object({
  address: z.string(),
  share: PercentSchema,
  verified: z.boolean().default(false)
});

/**
 * @internal
 */
const NFTCollectionMetadataInputSchema = CommonContractSchema.extend({
  creators: z.array(CreatorInputSchema).default([])
});

/**
 * @internal
 */

/// TOKEN ///

/**
 * @internal
 */
const TokenMetadataInputSchema = CommonContractSchema.extend({
  decimals: z.number().default(9),
  initialSupply: AmountSchema.superRefine((val, context) => {
    // TODO remove this limitation when metaplex fixes https://github.com/metaplex-foundation/js/issues/421
    if (Number(val) > 9999999) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Initial supply must less than 10M, additional supply can be minted after deployment.`,
        path: ["initialSupply"]
      });
    }
  })
});

/**
 * @public
 */

/**
 * @internal
 */
const CommonTokenInputSchema = z.object({
  name: z.string(),
  symbol: z.string(),
  decimals: z.number(),
  description: z.string().optional(),
  image: FileOrBufferOrStringSchema.optional(),
  external_link: z.string().url().optional()
});

/**
 * @internal
 */
const CurrencyValueSchema = z.object({
  value: z.string(),
  displayValue: z.string()
});

/**
 * Currency value and display value
 * @public
 */

/**
 * @internal
 */
CommonTokenInputSchema.extend({
  image: z.string().optional(),
  supply: CurrencyValueSchema
}).catchall(z.unknown());

/**
 * Metadata for a token
 * @public
 */

/**
 * @internal
 */
const NFTDropInitialConditionsInputSchema = z.object({
  totalSupply: AmountSchema
});

/**
 * @internal
 */
// TODO: Handle allow lists and end times
const NFTDropUpdateableConditionsInputSchema = z.object({
  price: AmountSchema.optional(),
  currencyAddress: z.string().nullable().optional(),
  primarySaleRecipient: z.string().optional(),
  sellerFeeBasisPoints: BasisPointsSchema.optional(),
  startTime: z.date().optional(),
  maxClaimable: QuantitySchema.optional()
});

/**
 * @internal
 */
const NFTDropUpdateableConditionsOutputSchema = z.object({
  price: CurrencyValueSchema,
  currencyAddress: z.string().nullable(),
  primarySaleRecipient: z.string(),
  sellerFeeBasisPoints: BasisPointsSchema,
  startTime: z.date().nullable(),
  totalAvailableSupply: z.number(),
  lazyMintedSupply: z.number(),
  claimedSupply: z.number(),
  maxClaimable: QuantitySchema,
  isReadyToClaim: z.boolean()
});

/**
 * @internal
 */
const NFTDropContractInputSchema = NFTCollectionMetadataInputSchema.merge(NFTDropInitialConditionsInputSchema);

/**
 * @public
 */

/**
 * @internal
 */
function enforceCreator() {
  let creators = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let owner = arguments.length > 1 ? arguments[1] : undefined;
  if (creators.length === 0) {
    // If no creators are specified, we assume the owner is the creator
    creators = creators.concat({
      address: owner.toBase58(),
      share: 100,
      verified: true
    });
  }
  return creators.map(creator => ({
    verified: creator.verified || false,
    address: new PublicKey(creator.address),
    share: creator.share
  }));
}

/**
 * @internal
 */
function parseCreators(creators) {
  return creators.map(creator => ({
    ...creator,
    address: creator.address.toBase58()
  }));
}

/**
 * Deploy new programs
 *
 * @example
 * ```jsx
 * import { ThirdwebSDK } from "@thirdweb-dev/sdk/solana";
 *
 * // Instantiate the SDK and pass in a signer
 * const sdk = ThirdwebSDK.fromNetwork("devnet");
 * sdk.wallet.connect(signer);
 *
 * // Define the metadata for your program
 * const metadata = {
 *   name: "NFT Contract",
 *   image: readFileSync("files/image.jpg"),
 * };
 *
 * // And deploy a new program from the connected wallet
 * const address = await sdk.deployer.createNftCollection(metadata);
 * ```
 *
 * @public
 */
class Deployer {
  constructor(registry, metaplex, storage) {
    _defineProperty(this, "metaplex", void 0);
    _defineProperty(this, "storage", void 0);
    _defineProperty(this, "regsitry", void 0);
    this.metaplex = metaplex;
    this.storage = storage;
    this.regsitry = registry;
  }

  /**
   * Create a new token program
   * @param tokenMetadata - the metadata of the token program
   * @returns - the address of the new token program
   *
   * @example
   * ```jsx
   * const metadata = {
   *   name: "My Token",
   *   symbol: "TKN",
   *   initialSupply: 100,
   * };
   *
   * const address = await sdk.deployer.createToken(metadata);
   * ```
   */
  async createToken(tokenMetadata) {
    const tokenMetadataParsed = TokenMetadataInputSchema.parse(tokenMetadata);
    const uri = await this.storage.upload(tokenMetadataParsed);
    const mint = Keypair.generate();
    const owner = this.metaplex.identity().publicKey;
    const mintTx = await this.metaplex.tokens().builders().createTokenWithMint({
      decimals: tokenMetadataParsed.decimals,
      initialSupply: token(tokenMetadataParsed.initialSupply, tokenMetadataParsed.decimals),
      mint
    });
    const name = tokenMetadataParsed.name;
    const data = {
      name,
      symbol: tokenMetadataParsed.symbol || "",
      sellerFeeBasisPoints: 0,
      uri,
      creators: enforceCreator([], this.metaplex.identity().publicKey),
      collection: null,
      uses: null
    };
    const metadata = findMetadataPda(mint.publicKey);
    const metaTx = createCreateMetadataAccountV2Instruction({
      metadata,
      mint: mint.publicKey,
      mintAuthority: owner,
      payer: owner,
      updateAuthority: owner
    }, {
      createMetadataAccountArgsV2: {
        data,
        isMutable: false
      }
    });
    const registryInstructions = await this.regsitry.getAddToRegistryInstructions(mint.publicKey, name, "token");
    await mintTx.add({
      instruction: metaTx,
      signers: [this.metaplex.identity()]
    }).append(...registryInstructions).sendAndConfirm(this.metaplex);
    return mint.publicKey.toBase58();
  }

  /**
   * Create a new NFT collection program
   * @param collectionMetadata - the metadata of the nft collection program
   * @returns - the address of the new nft collection program
   *
   * @example
   * ```jsx
   * const metadata = {
   *   name: "My NFT Collection",
   *   symbol: "NFT",
   * };
   *
   * const address = await sdk.deployer.createNftCollection(metadata);
   * ```
   */
  async createNftCollection(collectionMetadata) {
    const parsed = NFTCollectionMetadataInputSchema.parse(collectionMetadata);
    const uri = await this.storage.upload(parsed);
    const collectionMint = Keypair.generate();
    const name = parsed.name;
    const collectionTx = await this.metaplex.nfts().builders().create({
      useNewMint: collectionMint,
      name,
      symbol: parsed.symbol,
      sellerFeeBasisPoints: 0,
      uri,
      isCollection: true,
      creators: enforceCreator(parsed.creators, this.metaplex.identity().publicKey)
    });
    const registryInstructions = await this.regsitry.getAddToRegistryInstructions(collectionMint.publicKey, name, "nft-collection");
    const result = await collectionTx.append(...registryInstructions).sendAndConfirm(this.metaplex);
    if (!result.response.signature) {
      throw new Error("Transaction failed");
    }
    return collectionMint.publicKey.toBase58();
  }

  /**
   * Create a new NFT drop program
   * @param metadata - the metadata of the nft drop program
   * @returns - the address of the new nft drop program
   *
   * @example
   * ```jsx
   * const metadata = {
   *   name: "My NFT Drop",
   *   symbol: "NFT",
   *   totalSupply: 5,
   * };
   *
   * const address = await sdk.deployer.createNftDrop(metadata);
   * ```
   */
  async createNftDrop(metadata) {
    const collectionInfo = NFTCollectionMetadataInputSchema.parse(metadata);
    const candyMachineInfo = NFTDropInitialConditionsInputSchema.parse(metadata);
    const uri = await this.storage.upload(collectionInfo);
    const collectionMint = Keypair.generate();
    const name = collectionInfo.name;
    const collectionTx = await this.metaplex.nfts().builders().create({
      useNewMint: collectionMint,
      name,
      symbol: collectionInfo.symbol,
      sellerFeeBasisPoints: 0,
      uri,
      isCollection: true,
      creators: enforceCreator(collectionInfo.creators, this.metaplex.identity().publicKey)
    });
    const candyMachineKeypair = Keypair.generate();
    // initialize candy machine with default config
    // final claim conditions can be updated later
    const candyMachineTx = await this.metaplex.candyMachinesV2().builders().create({
      itemsAvailable: toBigNumber(candyMachineInfo.totalSupply),
      price: sol(0),
      sellerFeeBasisPoints: 0,
      endSettings: {
        endSettingType: 1,
        number: toBigNumber(0)
      },
      candyMachine: candyMachineKeypair,
      collection: collectionMint.publicKey,
      creators: enforceCreator(collectionInfo.creators, this.metaplex.identity().publicKey)
    });
    const registryInstructions = await this.regsitry.getAddToRegistryInstructions(candyMachineKeypair.publicKey, name, "nft-drop");

    // Have to split transactions here because it goes over the single transaction size limit
    // We use `signAllTransactions` to sign all the transactions at once so the user only has to sign once
    const block = await this.metaplex.connection.getLatestBlockhash();
    const dropTx = collectionTx.add(candyMachineTx).setFeePayer(this.metaplex.identity());
    const regTransaction = TransactionBuilder.make().add(...registryInstructions).setFeePayer(this.metaplex.identity()).toTransaction({
      blockhash: block.blockhash,
      lastValidBlockHeight: block.lastValidBlockHeight
    });
    const dropSigners = [this.metaplex.identity(), ...dropTx.getSigners()];
    const {
      keypairs
    } = getSignerHistogram(dropSigners);
    const dropTransaction = dropTx.toTransaction({
      blockhash: block.blockhash,
      lastValidBlockHeight: block.lastValidBlockHeight
    });
    // partially sign with the PDA keypairs
    if (keypairs.length > 0) {
      dropTransaction.partialSign(...keypairs);
    }
    // make the connected wallet sign both candyMachine + registry transactions
    const signedTx = await this.metaplex.identity().signAllTransactions([dropTransaction, regTransaction]);

    // send the signed transactions *sequentially* the drop creation needs to succeed first before adding to registry
    const signatures = [];
    for (const tx of signedTx) {
      signatures.push(await this.metaplex.connection.sendRawTransaction(tx.serialize()));
    }

    // wait for confirmations in parallel
    const confirmations = await Promise.all(signatures.map(sig => {
      return this.metaplex.rpc().confirmTransaction(sig, {
        blockhash: block.blockhash,
        lastValidBlockHeight: block.lastValidBlockHeight
      });
    }));
    if (confirmations.length === 0) {
      throw new Error("Transaction failed");
    }
    return candyMachineKeypair.publicKey.toBase58();
  }
}

/**
 * @internal
 */
const METAPLEX_PROGRAM_ID = "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s";

/**
 * @internal
 */
const CANDYMACHINE_PROGRAM_ID = "cndy3Z4yapfJBmL3ShUp5exZKqR3z33thTzeNMm2gRZ";

/**
 * @internal
 */
const TWREGISTRY_PROGRAM_ID = "twregzGdRmyFeAKjPgbPMkRkzgFNy8BHrB4HzwjyH14";

/**
 * @internal
 */
class NFTHelper {
  constructor(metaplex) {
    _defineProperty(this, "metaplex", void 0);
    _defineProperty(this, "connection", void 0);
    this.metaplex = metaplex;
    this.connection = metaplex.connection;
  }
  async getRaw(nftAddress) {
    return await this.metaplex.nfts().findByMint({
      mintAddress: new PublicKey(nftAddress)
    });
  }
  async get(nftAddress) {
    const meta = await this.getRaw(nftAddress);
    return await this.toNFTMetadata(meta);
  }
  async transfer(receiverAddress, nftAddress) {
    let quantity = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
    const result = await this.metaplex.nfts().send({
      mintAddress: new PublicKey(nftAddress),
      toOwner: new PublicKey(receiverAddress),
      amount: token(quantity, 0)
    });
    return {
      signature: result.response.signature
    };
  }
  async creatorsOf(nftAddress) {
    const meta = await this.getRaw(nftAddress);
    return parseCreators(meta.creators);
  }
  async balanceOf(walletAddress, nftAddress) {
    const address = await getAssociatedTokenAddress(new PublicKey(nftAddress), new PublicKey(walletAddress));
    try {
      const account = await getAccount(this.connection, address);
      return Number(account.amount);
    } catch (e) {
      return 0;
    }
  }
  async ownerOf(nftAddress) {
    try {
      const largestAccounts = await this.connection.getTokenLargestAccounts(new PublicKey(nftAddress));
      const largestAccountInfo = await this.connection.getParsedAccountInfo(largestAccounts.value[0].address);
      const parsedData = largestAccountInfo?.value?.data;
      const owner = parsedData ? parsedData.parsed.info.owner : undefined;
      return owner;
    } catch (err) {
      return undefined;
    }
  }
  async supplyOf(nftAddress) {
    let originalEdition;
    const originalEditionAccount = await this.metaplex.rpc().getAccount(findMasterEditionV2Pda(new PublicKey(nftAddress)));
    if (originalEditionAccount.exists) {
      originalEdition = toNftOriginalEdition(toOriginalEditionAccount(originalEditionAccount));
    } else {
      return 0;
    }

    // Add one to supply to account for the master edition
    return originalEdition.supply.toNumber() + 1;
  }
  async totalSupply(collectionAddress) {
    const metadataAddresses = await this.getAllMetadataAddresses(collectionAddress);
    return metadataAddresses.length;
  }
  async getAll(collectionAddress, queryParams) {
    const {
      start,
      count
    } = QueryAllParamsSchema.parse(queryParams);
    const metadataAddresses = await this.getAllMetadataAddresses(collectionAddress);

    // Metaplex GmaBuilder has a weird thing where they always start at 1 not 0
    // so we workaround it by adding an extra address, and shifting the count to get the actual count we want
    const fixedMetadataAddresses = (start === 0 ? [PublicKey.default] : []).concat(metadataAddresses);
    const metadataInfos = await GmaBuilder.make(this.metaplex, fixedMetadataAddresses).getBetween(start, start === 0 ? count + 1 : start + count);

    // Parse each account into a metadata account
    const metadataParsed = [];
    for (const metadataInfo of metadataInfos) {
      if (metadataInfo.exists) {
        try {
          metadataParsed.push(toMetadata(toMetadataAccount(metadataInfo)));
        } catch (error) {
          // no-op
        }
      }
    }

    // Finally, fetch the metadata + mint for each in parallel
    const nfts = await Promise.all(metadataParsed.map(m => this.toNFTMetadata(m)));
    return nfts;
  }
  async getAllMetadataAddresses(collectionAddress) {
    const collectionKey = new PublicKey(collectionAddress);

    // TODO cache signatures <> transactions mapping in memory so pagination doesn't re-request this everytime
    const allSignatures = [];
    // This returns the first 1000, so we need to loop through until we run out of signatures to get.
    let signatures = await this.metaplex.connection.getSignaturesForAddress(collectionKey);
    allSignatures.push(...signatures);
    do {
      const options = {
        before: signatures[signatures.length - 1]?.signature
      };
      signatures = await this.metaplex.connection.getSignaturesForAddress(collectionKey, options);
      allSignatures.push(...signatures);
    } while (signatures.length > 0);
    const metadataAddresses = [];

    // TODO RPC's will throttle this, need to do some optimizations here
    const batchSize = 1000; // alchemy RPC batch limit
    for (let i = 0; i < allSignatures.length; i += batchSize) {
      const batch = allSignatures.slice(i, Math.min(allSignatures.length, i + batchSize));
      const transactions = (await this.metaplex.connection.getTransactions(batch.map(s => s.signature))).reverse();
      for (const tx of transactions) {
        if (tx) {
          const programIds = tx.transaction.message.programIds().map(p => p.toString());
          const accountKeys = tx.transaction.message.accountKeys.map(p => p.toString());
          // Only look in transactions that call the Metaplex token metadata program
          if (programIds.includes(METAPLEX_PROGRAM_ID)) {
            // Go through all instructions in a given transaction
            for (const ix of tx.transaction.message.instructions) {
              // Filter for setAndVerify or verify instructions in the Metaplex token metadata program
              if ((ix.data === "K" || ix.data === "S" || ix.data === "X") && accountKeys[ix.programIdIndex] === METAPLEX_PROGRAM_ID) {
                const metadataAddressIndex = ix.accounts[0];
                const metadata_address = tx.transaction.message.accountKeys[metadataAddressIndex];
                metadataAddresses.push(metadata_address);
              }
            }
          } else if (programIds.includes(CANDYMACHINE_PROGRAM_ID)) {
            for (const ix of tx.transaction.message.instructions) {
              // filter for SetCollectionDuringMint from CandyMachineV2
              if (accountKeys[ix.programIdIndex] === CANDYMACHINE_PROGRAM_ID && ix.data === "JEuNFGs7wrU") {
                const metadataAddressIndex = ix.accounts[1];
                const metadata_address = tx.transaction.message.accountKeys[metadataAddressIndex];
                metadataAddresses.push(metadata_address);
              }
            }
          }
        }
      }
    }
    return metadataAddresses;
  }
  async toNFTMetadata(meta) {
    let mint = "mint" in meta ? meta.mint : undefined;
    let fullModel = meta;
    if (meta.model === "metadata") {
      fullModel = await this.getRaw(meta.mintAddress.toBase58());
      mint = fullModel.mint;
    }
    if (!mint) {
      throw new Error("No mint found for NFT");
    }
    const [owner, supply] = await Promise.all([this.ownerOf(mint.address.toBase58()), this.supplyOf(mint.address.toBase58())]);
    return this.toNFTMetadataResolved(mint, owner, supply, fullModel);
  }
  async toNFTMetadataResolved(mint, owner, supply, fullModel) {
    return {
      metadata: {
        id: mint.address.toBase58(),
        uri: fullModel.uri,
        name: fullModel.name,
        symbol: fullModel.symbol,
        ...fullModel.json
      },
      owner: owner || PublicKey.default.toBase58(),
      supply: supply,
      type: "metaplex"
    };
  }
}

async function sendMultipartTransaction(builders, metaplex) {
  const block = await metaplex.connection.getLatestBlockhash();
  const txns = builders.map(builder => {
    const builderTx = builder.setFeePayer(metaplex.identity());
    const dropSigners = [metaplex.identity(), ...builderTx.getSigners()];
    const {
      keypairs
    } = getSignerHistogram(dropSigners);
    const tx = builderTx.toTransaction({
      blockhash: block.blockhash,
      lastValidBlockHeight: block.lastValidBlockHeight
    });
    if (keypairs.length > 0) {
      tx.partialSign(...keypairs);
    }
    return tx;
  });

  // make the connected wallet sign all transactions
  const signedTx = await metaplex.identity().signAllTransactions(txns);

  // send the signed transactions in batches
  const batchSize = 200; // sends 200 raw tx in parallel
  const batches = [];
  for (let i = 0; i < signedTx.length; i += batchSize) {
    batches.push(signedTx.slice(i, i + batchSize));
  }
  let signatures = [];
  for (const txs of batches) {
    const signature = await Promise.all(txs.map(tx => metaplex.connection.sendRawTransaction(tx.serialize())));
    signatures.push(signature);
  }

  // wait for confirmations in parallel batches
  let confirmations = [];
  for (let i = 0; i < signatures.length; i += 1) {
    await delay(1000); // add delay to avoid rate limiting
    const sigs = signatures[i];
    const confirmationBatch = await Promise.all(sigs.map(sig => {
      return metaplex.rpc().confirmTransaction(sig, {
        blockhash: block.blockhash,
        lastValidBlockHeight: block.lastValidBlockHeight
      });
    }));
    confirmations.push(...confirmationBatch);
  }
  if (confirmations.length === 0) {
    throw new Error("Transaction failed");
  }
  return signatures.flatMap(sig => sig).map(signature => ({
    signature
  }));
}
async function delay(delayMs) {
  await new Promise(resolve => setTimeout(resolve, delayMs));
}

/**
 * @internal
 */
function getUrlForNetwork(network) {
  switch (network) {
    case "localnet":
      return "http://127.0.0.1:8899";
    case "testnet":
      return clusterApiUrl(network);
    case "mainnet-beta":
      return getRpcUrl("solana");
    case "devnet":
      return getRpcUrl("solana-devnet");
    default:
      return network;
  }
}
function getNework(metaplex) {
  const url = new URL(metaplex.connection.rpcEndpoint);
  // try this first to avoid hitting `custom` network for alchemy urls
  if (url.hostname.includes("devnet")) {
    return "devnet";
  }
  if (url.hostname.includes("mainnet")) {
    return "mainnet-beta";
  }
  return metaplex.cluster;
}
function getPublicRpc(metaplex) {
  const url = new URL(metaplex.connection.rpcEndpoint);
  if (url.hostname.includes("devnet")) {
    return "https://api.devnet.solana.com";
  }
  if (url.hostname.includes("mainnet")) {
    return "https://api.mainnet-beta.solana.com";
  }
  return url.toString();
}

/**
 * A collection of associated NFTs
 *
 * @example
 * ```jsx
 * import { ThirdwebSDK } from "@thirdweb-dev/sdk/solana";
 *
 * const sdk = ThirdwebSDK.fromNetwork("devnet");
 * sdk.wallet.connect(signer);
 *
 * // Get the interface for your NFT collection program
 * const program = await sdk.getProgram("{{program_address}}", "nft-collection");
 * ```
 *
 * @public
 */
class NFTCollection {
  get network() {
    return getNework(this.metaplex);
  }
  constructor(collectionAddress, metaplex, storage) {
    _defineProperty(this, "metaplex", void 0);
    _defineProperty(this, "storage", void 0);
    _defineProperty(this, "nft", void 0);
    _defineProperty(this, "publicKey", void 0);
    _defineProperty(this, "accountType", "nft-collection");
    this.storage = storage;
    this.metaplex = metaplex;
    this.nft = new NFTHelper(metaplex);
    this.publicKey = new PublicKey(collectionAddress);
  }

  /**
   * Get the metadata for this program.
   * @returns program metadata
   *
   * @example
   * ```jsx
   * const metadata = await program.getMetadata();
   * console.log(metadata);
   * ```
   */
  async getMetadata() {
    const metadata = await this.getCollection();
    return (await this.nft.toNFTMetadata(metadata)).metadata;
  }

  /**
   * Get the creators of this program.
   * @returns program metadata
   *
   * @example
   * ```jsx
   * const creators = await program.getCreators();
   * console.log(creators);
   * ```
   */
  async getCreators() {
    const metadata = await this.getCollection();
    return parseCreators(metadata.creators);
  }

  /**
   * Get the royalty basis points for this collection
   * @returns royalty basis points
   *
   * @example
   * ```jsx
   * const royalty = await program.getRoyalty();
   * console.log(royalty);
   * ```
   */
  async getRoyalty() {
    const metadata = await this.getCollection();
    return metadata.sellerFeeBasisPoints;
  }

  /**
   * Get the metadata for a specific NFT
   * @param nftAddress - the mint address of the NFT to get
   * @returns the metadata of the NFT
   *
   * @example
   * ```jsx
   * // Specify the mint address of the NFT to get the data of
   * const nftAddress = "...";
   * // And get the data for the NFT
   * const nft = await program.get(nftAddress);
   * console.log(nft.metadata.name);
   * console.log(nft.owner);
   * ```
   */
  async get(nftAddress) {
    return this.nft.get(nftAddress);
  }

  /**
   * Get the metadata for all NFTs on this collection
   * @remarks This method is paginated. Use the `start` and `count` properties of the queryParams object to control pagination. By default the first 100 NFTs are returned
   * @returns metadata for all minted NFTs
   *
   * @example
   * ```jsx
   * // Get all the NFTs that have been minted on this contract
   * const nfts = await program.getAll();
   * console.log(nfts[0].metadata.name);
   * console.log(nfts[0].owner);
   * ```
   */
  async getAll(queryParams) {
    return this.nft.getAll(this.publicKey.toBase58(), queryParams);
  }

  /**
   * Get the total number of nfts minted on this program
   * @returns the total number of NFTs minted on this program
   *
   * ```jsx
   * const totalSupply = await program.totalSupply();
   * ```
   */
  async totalSupply() {
    return this.nft.totalSupply(this.publicKey.toBase58());
  }

  /**
   * Get the NFT balance of the connected wallet
   * @returns the NFT balance
   *
   * @example
   * ```jsx
   * // The mint address of the NFT to check the balance of
   * const nftAddress = "..."
   * // Get the NFT balance of the currently connected wallet
   * const balance = await program.balance(nftAddress);
   * console.log(balance);
   * ```
   */
  async balance(nftAddress) {
    const address = this.metaplex.identity().publicKey.toBase58();
    return this.balanceOf(address, nftAddress);
  }

  /**
   * Get the NFT balance of the specified wallet
   * @param walletAddress - the wallet address to get the balance of
   * @param nftAddress - the mint address of the NFT to get the balance of
   * @returns the NFT balance
   *
   * @example
   * ```jsx
   * // Specify the address of the wallet to get the balance of
   * const walletAddress = "..."
   * // Specify the mint address of the NFT to get the balance of
   * const nftAddress = "..."
   * const balance = await program.balanceOf(walletAddress, nftAddress);
   * ```
   */
  async balanceOf(walletAddress, nftAddress) {
    return this.nft.balanceOf(walletAddress, nftAddress);
  }

  /**
   * Get the current owner of the given NFT
   * @param nftAddress - the mint address of the NFT to get the owner of
   * @returns the owner of the NFT
   * @example
   * ```jsx
   * const nftAddress = "..."
   * const owner = await program.ownerOf(nftAddress);
   * console.log(owner);
   * ```
   */
  async ownerOf(nftAddress) {
    return this.nft.ownerOf(nftAddress);
  }

  /**
   * Get the supply of NFT editions minted from a specific NFT
   * @param nftAddress - the mint address of the NFT to check the supply of
   * @returns the supply of the specified NFT
   *
   * @example
   * ```jsx
   * const address = "...";
   * const supply = await program.supplyOf(address);
   * ```
   */
  async supplyOf(nftAddress) {
    return this.nft.supplyOf(nftAddress);
  }

  /**
   * Transfer the specified NFTs to another wallet
   * @param receiverAddress - The address to send the tokens to
   * @param nftAddress - The mint address of the NFT to transfer
   * @returns the transaction result of the transfer
   *
   * @example
   * ```jsx
   * // The wallet address to transfer the NFTs to
   * const to = "...";
   * // The mint address of the NFT to transfer
   * const nftAddress = "...";
   * const tx = await program.transfer(to, nftAddress);
   * ```
   */
  async transfer(receiverAddress, nftAddress) {
    return this.nft.transfer(receiverAddress, nftAddress);
  }

  /**
   * Mint NFTs to the connected wallet
   * @param metadata - the metadata of the NFT to mint
   * @returns the mint address of the minted NFT
   *
   * @example
   * ```jsx
   * // Add the metadata of your NFT
   * const metadata = {
   *   name: "NFT #1",
   *   description: "My first NFT!",
   *   image: readFileSync("files/image.jpg"),
   *   properties: [
   *     {
   *        name: "coolness",
   *        value: "very cool!"
   *     }
   *   ]
   * }
   *
   * // Then mint the new NFT and get its address
   * const address = await program.mint(metadata);
   * console.log(address);
   * ```
   */
  async mint(metadata) {
    const address = this.metaplex.identity().publicKey.toBase58();
    return this.mintTo(address, metadata);
  }

  /**
   * Mint an NFT to the specified wallet
   * @param to - the address to mint the NFT to
   * @param metadata - the metadata of the NFT to mint
   * @returns the mint address of the minted NFT
   *
   * @example
   * ```jsx
   * // Specify who to mint the NFT to
   * const to = "...";
   *
   * // Add the metadata of your NFT
   * const metadata = {
   *   name: "NFT #1",
   *   description: "My first NFT!",
   *   image: readFileSync("files/image.jpg"),
   *   properties: [
   *     {
   *        name: "coolness",
   *        value: "very cool!"
   *     }
   *   ]
   * }
   *
   * // Then mint the new NFT and get its address
   * const address = await program.mintTo(to, metadata);
   * console.log(address);
   * ```
   */
  async mintTo(to, metadata) {
    // TODO add options param for initial/maximum supply
    const uri = typeof metadata === "string" ? metadata : await this.storage.upload(metadata);
    const name = typeof metadata === "string" ? "" : metadata.name?.toString() || "";
    const collection = await this.getCollection();
    const {
      nft
    } = await this.metaplex.nfts().create({
      name,
      uri,
      creators: collection.creators,
      sellerFeeBasisPoints: collection.sellerFeeBasisPoints,
      collection: this.publicKey,
      collectionAuthority: this.metaplex.identity(),
      tokenOwner: new PublicKey(to),
      // Always sets max supply to unlimited so editions can be minted
      maxSupply: null
    });
    return nft.address.toBase58();
  }

  /**
   * Mint additional supply of an NFT to the connected wallet
   * @param nftAddress - the mint address to mint additional supply to
   * @param amount - the amount of additional NFTs to mint
   * @returns the mint address of the minted NFT
   *
   * @example
   * ```jsx
   * // The address of the already minted NFT
   * const nftAddress = "..."
   * // The amount of additional NFTs to mint
   * const amount = 1;
   * // Mint an additional NFT of the original NFT
   * const addresses = await program.mintAdditionalSupply(nftAddress, amount);
   * ```
   */
  async mintAdditionalSupply(nftAddress, amount) {
    const address = this.metaplex.identity().publicKey.toBase58();
    return this.mintAdditionalSupplyTo(address, nftAddress, amount);
  }

  /**
   * Mint additional supply of an NFT to the specified wallet
   * @param to - the address to mint the NFT to
   * @param nftAddress - the mint address to mint additional supply to
   * @param amount - the amount of NFTs to mint
   * @returns the mint address of the minted NFT
   *
   * @example
   * ```jsx
   * // Specify who to mint the additional NFT to
   * const to = "..."
   * // The address of the already minted NFT
   * const nftAddress = "..."
   * * // The amount of additional NFTs to mint
   * const amount = 1;
   * // Mint an additional NFT of the original NFT
   * const addresses = await program.mintAdditionalSupplyTo(to, nftAddress, amount);
   * ```
   */
  async mintAdditionalSupplyTo(to, nftAddress, amount) {
    // need to coerce amount to number always (to make the Array(<number>) work)
    amount = typeof amount === "string" ? parseInt(amount) : amount;
    // ensure that whatever got passed actually is a number now
    invariant(isNaN(amount) === false, "amount must be possible to convert to a number");

    // Better to use metaplex functions directly then our supplyOf function for types/consistency
    const originalEditionAccount = await this.metaplex.rpc().getAccount(findMasterEditionV2Pda(new PublicKey(nftAddress)));
    const originalEdition = toNftOriginalEdition(toOriginalEditionAccount(originalEditionAccount));
    const builders = await Promise.all([...Array(amount).keys()].map(async (_, i) => {
      return await this.metaplex.nfts().builders().printNewEdition({
        originalSupply: toBigNumber(originalEdition.supply.add(toBigNumber(i))),
        originalMint: new PublicKey(nftAddress),
        newOwner: new PublicKey(to)
      });
    }));
    const mintAddresses = builders.map(builder => builder.getContext().mintSigner.publicKey.toBase58());
    await sendMultipartTransaction(builders, this.metaplex);
    return mintAddresses;
  }

  /**
   * Burn an NFT
   * @param nftAddress - the mint address of the NFT to burn
   * @returns the transaction signature
   *
   * @example
   * ```jsx
   * // Specify the address of the NFT to burn
   * const nftAddress = "..."
   * // And send the actual burn transaction
   * const tx = await program.burn(nftAddress);
   * ```
   */
  async burn(nftAddress) {
    const tx = await this.metaplex.nfts().delete({
      mintAddress: new PublicKey(nftAddress),
      collection: this.publicKey
    });
    return {
      signature: tx.response.signature
    };
  }

  /**
   * SETTINGS
   */

  /**
   * Update the creators of the collection
   * @param creators - the creators to update
   * @param updateAll - whether or not to retroactively update the creators of all past NFTs
   */
  async updateCreators(creators) {
    let updateAll = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (updateAll) {
      const txs = [];
      txs.push(this.metaplex.nfts().builders().update({
        nftOrSft: await this.getCollection(),
        creators: enforceCreator(creators, this.metaplex.identity().publicKey)
      }));

      // TODO: Don't use a get all here, all we need is mint addresses :(
      const allNfts = await this.nft.getAll(this.publicKey.toBase58());
      await Promise.all(allNfts.map(async nft => {
        const metaplexNft = await this.metaplex.nfts().findByMint({
          mintAddress: new PublicKey(nft.metadata.id)
        });
        txs.push(this.metaplex.nfts().builders().update({
          collection: this.publicKey,
          nftOrSft: metaplexNft,
          creators: enforceCreator(creators, this.metaplex.identity().publicKey)
        }));
      }));
      return await sendMultipartTransaction(txs, this.metaplex);
    } else {
      const tx = await this.metaplex.nfts().update({
        nftOrSft: await this.getCollection(),
        creators: enforceCreator(creators, this.metaplex.identity().publicKey)
      });
      return [{
        signature: tx.response.signature
      }];
    }
  }

  /**
   * Update the royalty basis points of the collection
   * @param sellerFeeBasisPoints - the royalty basis points of the collection
   * @param updateAll - whether or not to retroactively update the royalty basis points of all past NFTs
   */
  async updateRoyalty(sellerFeeBasisPoints) {
    let updateAll = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (updateAll) {
      const txs = [];
      txs.push(this.metaplex.nfts().builders().update({
        nftOrSft: await this.getCollection(),
        sellerFeeBasisPoints
      }));

      // TODO: Don't use a get all here, all we need is mint addresses :(
      const allNfts = await this.nft.getAll(this.publicKey.toBase58());
      await Promise.all(allNfts.map(async nft => {
        const metaplexNft = await this.metaplex.nfts().findByMint({
          mintAddress: new PublicKey(nft.metadata.id)
        });
        txs.push(this.metaplex.nfts().builders().update({
          collection: this.publicKey,
          nftOrSft: metaplexNft,
          sellerFeeBasisPoints
        }));
      }));
      return await sendMultipartTransaction(txs, this.metaplex);
    } else {
      const tx = await this.metaplex.nfts().update({
        nftOrSft: await this.getCollection(),
        sellerFeeBasisPoints
      });
      return [{
        signature: tx.response.signature
      }];
    }
  }
  async getCollection() {
    return await this.metaplex.nfts().findByMint({
      mintAddress: this.publicKey
    });
  }
}

/**
 * Set the claim conditions for your NFT Drop to control how people can claim NFTs
 *
 * @example
 * ```jsx
 * const program = await sdk.getProgram("{{program_address}}", "nft-drop");
 *
 * // Get the data of the claim condition on the drop
 * const claimCondition = await program.claimConditions.get();
 * // View the price of the drop
 * console.log(claimCondition.price);
 * // View the date when the drop will go live
 * console.log(claimCondition.goLiveDate);
 * ```
 *
 * @public
 */
class ClaimConditions {
  constructor(dropAddress, metaplex) {
    _defineProperty(this, "dropMintAddress", void 0);
    _defineProperty(this, "metaplex", void 0);
    this.dropMintAddress = new PublicKey(dropAddress);
    this.metaplex = metaplex;
  }

  /**
   * Get the claim condition for this contract
   * @returns - The claim condition data for this NFT Drop
   *
   * @example
   * ```jsx
   * // Get the data of the claim condition on the drop
   * const claimCondition = await program.claimConditions.get();
   * // View the price of the drop
   * console.log(claimCondition.price);
   * // View the date when the drop will go live
   * console.log(claimCondition.goLiveDate);
   * ```
   */
  async get() {
    const candyMachine = await this.getCandyMachine();
    const totalAvailableSupply = candyMachine.itemsAvailable.toNumber();
    const lazyMintedSupply = candyMachine.itemsLoaded.toNumber();
    const goLiveDate = candyMachine.goLiveDate ? new Date(candyMachine.goLiveDate.toNumber() * 1000) : null;
    const isWithinGoLiveDate = goLiveDate ? goLiveDate <= new Date() : true;
    const maxClaimable = candyMachine.endSettings && candyMachine.endSettings.endSettingType === 1 ? candyMachine.endSettings.number.toNumber() : "unlimited";
    const claimedSupply = candyMachine.itemsMinted.toNumber();
    const isBelowMaxClaimable = maxClaimable !== "unlimited" ? claimedSupply < maxClaimable : claimedSupply < lazyMintedSupply;

    // TODO add allowlist/hidden settings here
    return {
      price: toCurrencyValue(candyMachine.price),
      currencyAddress: candyMachine.tokenMintAddress?.toBase58() || null,
      primarySaleRecipient: candyMachine.walletAddress.toBase58(),
      sellerFeeBasisPoints: candyMachine.sellerFeeBasisPoints,
      startTime: goLiveDate,
      totalAvailableSupply,
      lazyMintedSupply,
      claimedSupply,
      maxClaimable: maxClaimable.toString(),
      isReadyToClaim: candyMachine.isFullyLoaded && isWithinGoLiveDate && isBelowMaxClaimable
    };
  }
  async assertCanClaimable(quantity) {
    const conditions = await this.get();
    if (!conditions.isReadyToClaim) {
      if (conditions.lazyMintedSupply < conditions.totalAvailableSupply) {
        throw new Error(`NFT Drop is not fully loaded yet. Only ${conditions.lazyMintedSupply} out of ${conditions.totalAvailableSupply} NFTs have been lazy minted.`);
      }
      if (conditions.maxClaimable === "0") {
        throw new Error(`Max Claimable is 0. No NFTs can be claimed. Set your claim conditions to enable claiming.`);
      }
      const maxClaimable = conditions.maxClaimable === "unlimited" ? null : parseInt(conditions.maxClaimable);
      if (maxClaimable !== null && conditions.claimedSupply + quantity > maxClaimable || conditions.claimedSupply + quantity > conditions.lazyMintedSupply) {
        throw new Error(`Max claimable reached - ${conditions.claimedSupply} out of ${conditions.maxClaimable !== null ? conditions.maxClaimable : conditions.lazyMintedSupply} NFTs have been claimed.`);
      }
      if (conditions.startTime && conditions.startTime > new Date()) {
        throw new Error(`Drop is not to ready be claimed yet, start date is ${conditions.startTime}`);
      }
    }
  }

  /**
   * Set the claim condition settings to configure how people can claim your NFT Drop
   * @param metadata - The settings to use for the claim condition of this program
   * @returns - The transaction result of setting the claim condition
   *
   * @example
   * ```jsx
   * // Specify the settings for your claim condition
   * const claimCondition = {
   *   // The price of each NFT in this drop (in SOL or SPL tokens)
   *   price: 0.1,
   *   // The date for this drop to start
   *   goLiveDate: new Date(),
   *   // ...
   * };
   *
   * const tx = await program.claimConditions.set(claimCondition);
   * ```
   */
  async set(metadata) {
    const parsed = NFTDropUpdateableConditionsInputSchema.parse(metadata);

    // recipients
    let wallet = parsed.primarySaleRecipient ? new PublicKey(parsed.primarySaleRecipient) : undefined;
    const sellerFeeBasisPoints = parsed.sellerFeeBasisPoints;

    // price
    let price = parsed.price ? sol(Number(parsed.price)) : undefined;
    let tokenMint = undefined;
    if (parsed.currencyAddress) {
      const fetchedToken = await this.metaplex.tokens().findMintByAddress({
        address: new PublicKey(parsed.currencyAddress)
      });
      price = token(Number(parsed.price || 0), fetchedToken.decimals);
      tokenMint = fetchedToken.address;
      wallet = this.metaplex.tokens().pdas().associatedTokenAccount({
        mint: tokenMint,
        owner: wallet ? wallet : this.metaplex.identity().publicKey
      });
    }

    // dates
    const goLiveDate = parsed.startTime ? toDateTime(parsed.startTime) : undefined;

    // max claimable
    const endSettings = parsed.maxClaimable ? parsed.maxClaimable === "unlimited" ? null : {
      endSettingType: 1,
      number: toBigNumber(parsed.maxClaimable)
    } : undefined;

    // TODO add allowlist/hidden settings here

    const data = {
      ...(wallet && {
        wallet
      }),
      ...(tokenMint ? {
        tokenMint
      } :
      // if passing currencyAddress explcitly as null we need to honor that!
      parsed.currencyAddress === null ? {
        tokenMint: null
      } : {}),
      ...(price && {
        price
      }),
      ...(goLiveDate && {
        goLiveDate
      }),
      ...(sellerFeeBasisPoints && {
        sellerFeeBasisPoints
      }),
      ...(endSettings !== undefined && {
        endSettings
      })
    };
    const result = await this.metaplex.candyMachinesV2().update({
      candyMachine: await this.getCandyMachine(),
      ...data
    });
    return {
      signature: result.response.signature
    };
  }
  async getCandyMachine() {
    return this.metaplex.candyMachinesV2().findByAddress({
      address: this.dropMintAddress
    });
  }
}

const LAZY_MINT_BATCH_SIZE = 5;

/**
 * A collection of NFTs that can be lazy minted and claimed
 *
 * @example
 * ```jsx
 * import { ThirdwebSDK } from "@thirdweb-dev/sdk/solana";
 *
 * const sdk = ThirdwebSDK.fromNetwork("devnet");
 * sdk.wallet.connect(signer);
 *
 * // Get the interface for your NFT Drop program
 * const program = await sdk.getProgram("{{program_address}}", "nft-drop");
 * ```
 *
 * @public
 */
class NFTDrop {
  get network() {
    return getNework(this.metaplex);
  }

  /**
   * Manage the claim conditions for this drop
   *
   * @example
   * ```jsx
   * // set your claim conditions
   * await program.claimConditions.set({
   *  maxClaimable: 100,
   *  price: 0.5,
   *  startTime: new Date(),
   * });
   *
   * // get your claim conditions
   * const conditions = await program.claimConditions.get();
   * console.log(conditions.maxClaimable);
   * console.log(conditions.claimedSupply);
   * ```
   */

  constructor(dropAddress, metaplex, storage) {
    _defineProperty(this, "metaplex", void 0);
    _defineProperty(this, "storage", void 0);
    _defineProperty(this, "nft", void 0);
    _defineProperty(this, "accountType", "nft-drop");
    _defineProperty(this, "publicKey", void 0);
    _defineProperty(this, "claimConditions", void 0);
    this.storage = storage;
    this.metaplex = metaplex;
    this.nft = new NFTHelper(metaplex);
    this.publicKey = new PublicKey(dropAddress);
    this.claimConditions = new ClaimConditions(dropAddress, metaplex);
  }

  /**
   * Get the metadata for this program.
   * @returns program metadata
   *
   * @example
   * ```jsx
   * const metadata = await program.getMetadata();
   * console.log(metadata.name);
   * ```
   */
  async getMetadata() {
    const info = await this.getCandyMachine();
    invariant(info.collectionMintAddress, "Collection mint address not found");
    const metadata = await this.metaplex.nfts().findByMint({
      mintAddress: info.collectionMintAddress
    });
    return (await this.nft.toNFTMetadata(metadata)).metadata;
  }

  /**
   * Get the creators of this program.
   * @returns program metadata
   *
   * @example
   * ```jsx
   * const creators = await program.getCreators();
   * console.log(creators);
   * ```
   */
  async getCreators() {
    const info = await this.getCandyMachine();
    return parseCreators(info.creators);
  }

  /**
   * Get the royalty basis points for this collection
   * @returns royalty basis points
   *
   * @example
   * ```jsx
   * const royalty = await program.getRoyalty();
   * console.log(royalty);
   * ```
   */
  async getRoyalty() {
    const info = await this.getCandyMachine();
    return info.sellerFeeBasisPoints;
  }

  /**
   * Get the metadata for a specific NFT
   * @param nftAddress - the mint address of the NFT to get
   * @returns the metadata of the NFT
   *
   * @example
   * ```jsx
   * // Specify the mint address of the NFT to get the data of
   * const nftAddress = "...";
   * // And get the data for the NFT
   * const nft = await program.get(nftAddress);
   *
   * console.log(nft.name);
   * ```
   */
  async get(nftAddress) {
    return this.nft.get(nftAddress);
  }

  /**
   * Get the metadata for all NFTs on this drop
   * @returns metadata for all minted NFTs
   *
   * @example
   * ```jsx
   * // Get all the NFTs that have been minted on this contract
   * const nfts = await program.getAll();
   *
   * console.log(nfts[0].metadata.name);
   * ```
   */
  async getAll(queryParams) {
    const parsedQueryParams = QueryAllParamsSchema.parse(queryParams);
    const info = await this.getCandyMachine();

    // First, get all the claimed NFTs within the query params range
    const claimedNfts = await this.getAllClaimed(parsedQueryParams);
    const totalClaimed = await this.totalClaimedSupply();

    // Then filter out all claimed NFTs from items to leave only unclaimed remaining
    const unclaimedItems = [];
    info.items.forEach(item => {
      const isClaimed = claimedNfts.filter(nft => nft.metadata.name === item.name && nft.metadata.uri === item.uri).length > 0;
      if (!isClaimed) {
        unclaimedItems.push(item);
      }
    });

    // Only fill the remaining count left over after claimed NFTs with unclaimed NFTs
    const startIndex = Math.max(0, parsedQueryParams.start - totalClaimed);
    const endIndex = Math.max(0, startIndex + parsedQueryParams.count - claimedNfts.length);
    const unclaimedNfts = await Promise.all(unclaimedItems.slice(startIndex, endIndex).map(async item => {
      const metadata = await this.storage.downloadJSON(item.uri);
      return {
        metadata: {
          ...metadata,
          id: PublicKey.default.toBase58(),
          uri: item.uri
        },
        owner: PublicKey.default.toBase58(),
        supply: 0,
        type: "metaplex"
      };
    }));

    // Always return claimed NFTs first, and then fill remaining query count with unclaimed NFTs
    return [...claimedNfts, ...unclaimedNfts];
  }

  /**
   * Get the metadata for all the claimed NFTs on this drop
   * @returns metadata for all claimed NFTs
   *
   * @example
   * ```jsx
   * // Get all the NFTs that have already been claimed from this drop
   * const nfts = await program.getAllClaimed();
   * console.log(nfts[0].name)
   * ```
   */
  async getAllClaimed(queryParams) {
    // using getAll from collection here because candy machin findAllMinted doesn't return anything
    const candy = await this.getCandyMachine();
    invariant(candy.collectionMintAddress, "Collection mint address not found");
    return await this.nft.getAll(candy.collectionMintAddress.toBase58(), queryParams);
  }

  /**
   * Get the NFT balance of the connected wallet
   * @returns the NFT balance
   *
   * @example
   * ```jsx
   * // The mint address of the NFT to check the balance of
   * const nftAddress = "..."
   * // Get the NFT balance of the currently connected wallet
   * const balance = await program.balance(nftAddress);
   * console.log(balance);
   * ```
   */
  async balance(nftAddress) {
    const address = this.metaplex.identity().publicKey.toBase58();
    return this.balanceOf(address, nftAddress);
  }

  /**
   * Get the NFT balance of the specified wallet
   * @param walletAddress - the wallet address to get the balance of
   * @param nftAddress - the mint address of the NFT to get the balance of
   * @returns the NFT balance
   *
   * @example
   * ```jsx
   * // The address of the wallet to check the balance of
   * const walletAddress = "..."
   * // The mint address of the NFT to check the balance of
   * const nftAddress = "..."
   * // Get the actual NFT balance of the specified wallet
   * const balance = await program.balanceOf(walletAddress, nftAddress);
   * ```
   */
  async balanceOf(walletAddress, nftAddress) {
    return this.nft.balanceOf(walletAddress, nftAddress);
  }

  /**
   * Get the current owner of the given NFT
   * @param nftAddress - the mint address of the NFT to get the owner of
   * @returns the owner of the NFT
   * @example
   * ```jsx
   * const nftAddress = "..."
   * const owner = await program.ownerOf(nftAddress);
   * console.log(owner);
   * ```
   */
  async ownerOf(nftAddress) {
    return this.nft.ownerOf(nftAddress);
  }

  /**
   * Get the total minted supply of this drop
   * @returns the total supply
   *
   * @example
   * ```jsx
   * // Get the total number of NFTs that have been minted on this drop
   * const supply = await program.totalSupply();
   * ```
   */
  async totalSupply() {
    const info = await this.getCandyMachine();
    return info.itemsLoaded.toNumber();
  }

  /**
   * Get the total unclaimed supply of this drop
   * @returns the total supply
   *
   * @example
   * ```jsx
   * // Get the total number of lazy minted NFTs that aren't yet claimed
   * const supply = await program.totalUnclaimedSupply();
   * ```
   */
  async totalUnclaimedSupply() {
    const info = await this.getCandyMachine();
    return Math.min(info.itemsLoaded.toNumber(), info.itemsRemaining.toNumber());
  }

  /**
   * Get the total claimed supply of this drop
   * @returns the total supply
   *
   * @example
   * ```jsx
   * // Get the total number of lazy minted NFTs that have already been claimed
   * const supply = await program.totalClaimedSupply();
   * ```
   */
  async totalClaimedSupply() {
    const info = await this.getCandyMachine();
    return info.itemsMinted.toNumber();
  }

  /**
   * Transfer the specified NFTs to another wallet
   * @param receiverAddress - The address to send the tokens to
   * @param nftAddress - The mint address of the NFT to transfer
   * @returns the transaction result of the transfer
   *
   * @example
   * ```jsx
   * // The wallet address to transfer the NFTs to
   * const to = "...";
   * // The mint address of the NFT to transfer
   * const nftAddress = "...";
   * const tx = await program.transfer(to, nftAddress);
   * ```
   */
  async transfer(receiverAddress, nftAddress) {
    return this.nft.transfer(receiverAddress, nftAddress);
  }

  /**
   * Lazy mint NFTs to be claimed later
   * @param metadatas - The metadata of the NFTs to lazy mint
   * @param options
   * @returns the transaction result of the lazy mint
   *
   * @example
   * ```jsx
   * // Add the metadata of your NFTs
   * const metadata = [
   *   {
   *     name: "NFT #1",
   *     description: "My first NFT!",
   *     image: readFileSync("files/image.jpg"),
   *     properties: [
   *       {
   *         name: "coolness",
   *         value: "very cool!"
   *       }
   *     ]
   *   }
   * ];
   *
   * // And lazy mint NFTs to your program
   * const tx = await program.lazyMint(metadatas);
   * ```
   */
  async lazyMint(metadatas, options) {
    const candyMachine = await this.getCandyMachine();
    const parsedMetadatas = metadatas.map(metadata => CommonNFTInput.parse(metadata));
    const uris = await this.storage.uploadBatch(parsedMetadatas, options);
    const items = uris.map((uri, i) => ({
      name: parsedMetadatas[i].name?.toString() || "",
      uri
    }));

    // turn items into batches of $LAZY_MINT_BATCH_SIZE
    const batches = [];
    while (items.length) {
      batches.push(items.splice(0, LAZY_MINT_BATCH_SIZE));
    }
    const builders = batches.map((batch, i) => this.metaplex.candyMachinesV2().builders().insertItems({
      candyMachine,
      authority: this.metaplex.identity(),
      items: batch,
      index: toBigNumber(i * LAZY_MINT_BATCH_SIZE + candyMachine.itemsLoaded.toNumber())
    }));
    return await sendMultipartTransaction(builders, this.metaplex);
  }

  /**
   * Claim an NFT from the drop with connected wallet
   * @returns - the mint address of the claimed NFT
   *
   * @example
   * ```jsx
   * // Specify the quantity of NFTs to claim
   * const quantity = 1;
   * // Claim NFTs and get their mint addresses
   * const claimedAddresses = await program.claim(quantity);
   * console.log("Claimed NFT at address", claimedAddresses[0]);
   * ```
   */
  async claim(amount) {
    const address = this.metaplex.identity().publicKey.toBase58();
    return this.claimTo(address, amount);
  }

  /**
   * Claim an NFT from the drop for the specified wallet
   * @returns - the mint address of the claimed NFT
   *
   * @example
   * ```jsx
   * // Specify which address to claim the NFTs to
   * const receiverAddress =  "...";
   * // Claim the NFTs to the specified wallet and get the mint addresses of the NFTs
   * const claimedAddresses = await program.claimTo(receiverAddress, 1);
   * console.log("Claimed NFT at address", claimedAddresses[0]);
   * ```
   */
  async claimTo(receiverAddress, amount) {
    const candyMachine = await this.getCandyMachine();
    await this.claimConditions.assertCanClaimable(Number(amount));
    const builders = await Promise.all([...Array(Number(amount)).keys()].map(async () => {
      return await this.metaplex.candyMachinesV2().builders().mint({
        candyMachine,
        newOwner: new PublicKey(receiverAddress)
      });
    }));
    const mintAddresses = builders.map(builder => builder.getContext().mintSigner.publicKey.toBase58());
    await sendMultipartTransaction(builders, this.metaplex);
    return mintAddresses;
  }

  /**
   * Burn an NFT
   * @param nftAddress - the mint address of the NFT to burn
   * @returns the transaction signature
   *
   * @example
   * ```jsx
   * // Specify the address of the NFT to burn
   * const nftAddress = "..."
   * // And send the actual burn transaction
   * const tx = await program.burn(nftAddress);
   * ```
   */
  async burn(nftAddress) {
    const candyMachine = await this.getCandyMachine();
    const collection = candyMachine.collectionMintAddress ? candyMachine.collectionMintAddress : undefined;
    const tx = await this.metaplex.nfts().delete({
      mintAddress: new PublicKey(nftAddress),
      collection
    });
    return {
      signature: tx.response.signature
    };
  }

  /**
   * Update the creators of the collection
   * @param creators - the creators to update
   */
  async updateCreators(creators) {
    const tx = await this.metaplex.candyMachinesV2().update({
      candyMachine: await this.getCandyMachine(),
      creators: enforceCreator(creators, this.metaplex.identity().publicKey)
    });
    return {
      signature: tx.response.signature
    };
  }

  /**
   * Update the royalty basis points of the collection
   * @param sellerFeeBasisPoints - the royalty basis points of the collection
   */
  async updateRoyalty(sellerFeeBasisPoints) {
    const tx = await this.metaplex.candyMachinesV2().update({
      candyMachine: await this.getCandyMachine(),
      sellerFeeBasisPoints
    });
    return {
      signature: tx.response.signature
    };
  }
  async getCandyMachine() {
    return this.metaplex.candyMachinesV2().findByAddress({
      address: this.publicKey
    });
  }
}

/**
 * Dynamic interface for interacting with Solana programs.
 *
 * @example
 * ```jsx
 * import { ThirdwebSDK } from "@thirdweb-dev/sdk/solana";
 *
 * const sdk = ThirdwebSDK.fromNetwork("devnet");
 * sdk.wallet.connect(signer);
 *
 * // Get the interface for your program
 * const program = await sdk.getProgram("{{program_address}}");
 * ```
 *
 * @public
 */
class Program {
  constructor(programAddress, idl, connection, wallet) {
    _defineProperty(this, "program", void 0);
    _defineProperty(this, "publicKey", void 0);
    _defineProperty(this, "accountType", "program");
    _defineProperty(this, "network", void 0);
    this.publicKey = new PublicKey(programAddress);
    this.network = resolveClusterFromConnection(connection);
    setProvider(new AnchorProvider(connection, wallet.getSigner(), {}));
    this.program = new Program$1(idl, programAddress);
    wallet.events.on("connected", () => {
      setProvider(new AnchorProvider(connection, wallet.getSigner(), {}));
    });
    wallet.events.on("disconnected", () => {
      // identity will be guest in this case
      setProvider(new AnchorProvider(connection, wallet.getSigner(), {}));
    });
  }

  /**
   * Call a function on this program
   * @param functionName - Name of the function to call
   * @param args - Arguments to pass to the function including accounts, data, and signers
   * @returns result of the contract call
   *
   * @example
   * ```jsx
   * const counterAccount = Keypair.generate();
   * await program.call("increment", {
   *   // We need to pass in the public keys of any accounts to interact with
   *   accounts: {
   *     counterAccount: counterAccount.publicKey.toBase58(),
   *   },
   *   // As well as the arguments to pass to the data parameters
   *   data: ["..."],
   *   // And the signer of the account that will be signing the message
   *   signers: [counterAccount]
   * })
   * ```
   */
  async call(functionName, args) {
    return await this.prepareCall(functionName, args).rpc();
  }
  prepareCall(functionName, args) {
    const fn = this.program.methods[functionName];
    if (!fn) {
      throw new Error(`Function ${functionName} not found`);
    }
    const fnWithArgs = args.data ? fn(...args.data) : fn();
    return fnWithArgs.accounts(args.accounts).signers(args.signers || []);
  }

  /**
   * Read account data associated with this program
   * @param accountName - The name of the account type to fetch the data of
   * @param address - The address of the specific account to fetch
   * @returns - The data of the requested account
   *
   * @example
   * ```jsx
   * const accountAddress = "...";
   * // Get the counterAccount at specified address
   * const counterAccount = await program.fetch("counterAccount", accountaddress);
   * ```
   */
  async fetch(accountName, address) {
    const account = this.program.account[accountName];
    if (!account) {
      throw new Error(`Account ${account} not found`);
    }
    return await account.fetch(address);
  }

  /**
   * Read multiple accounts data associated with this program
   * @param accountName - The name of the account type to fetch the data of
   * @param addresses - The addresses of the each account to fetch
   * @returns - The data of the requested accounts
   *
   * @example
   * ```jsx
   * const accountAddresses = ["...", "..."];
   * const counterAccounts = await program.fetchMultiple("counterAccount", accountAddresses);
   * ```
   */
  async fetchMultiple(accountName, addresses) {
    const account = this.program.account[accountName];
    if (!account) {
      throw new Error(`Account ${account} not found`);
    }
    return (await account.fetchMultiple(addresses)).filter(a => a !== null);
  }
}

var program = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Program: Program
});

/**
 * Standard token or cryptocurrency.
 *
 * @example
 * ```jsx
 * import { ThirdwebSDK } from "@thirdweb-dev/sdk/solana";
 *
 * const sdk = ThirdwebSDK.fromNetwork("devnet");
 * sdk.wallet.connect(signer);
 *
 * // Get the interface for your token program
 * const program = await sdk.getProgram("{{program_address}}", "token");
 * ```
 *
 * @public
 */
class Token {
  get network() {
    return getNework(this.metaplex);
  }
  constructor(tokenAddress, metaplex, storage) {
    _defineProperty(this, "connection", void 0);
    _defineProperty(this, "metaplex", void 0);
    _defineProperty(this, "storage", void 0);
    _defineProperty(this, "accountType", "token");
    _defineProperty(this, "publicKey", void 0);
    this.storage = storage;
    this.metaplex = metaplex;
    this.connection = metaplex.connection;
    this.publicKey = new PublicKey(tokenAddress);
  }

  /**
   * Get the metadata for this token including the name, supply, and decimals.
   * @returns Token metadata
   *
   * @example
   * ```jsx
   * const metadata = await program.getMetadata();
   * console.log(metadata.supply);
   * console.log(metadata.decimals);
   * ```
   */
  async getMetadata() {
    const mint = await this.getMint();
    const addr = findMetadataPda(this.publicKey);
    const account = await this.metaplex.rpc().getAccount(addr);
    const meta = toMetadata(toMetadataAccount(account));
    return {
      ...meta.json,
      id: meta.address.toBase58(),
      uri: meta.uri,
      name: meta.name,
      symbol: meta.symbol,
      decimals: mint.decimals,
      supply: toCurrencyValue(mint.supply)
    };
  }

  /**
   * Get the total minted supply of this token
   * @returns the total supply
   *
   * @example
   * ```jsx
   * const supply = await program.totalSupply();
   * ```
   */
  async totalSupply() {
    const info = await this.getMint();
    return toCurrencyValue(info.supply);
  }

  /**
   * Mints the specified amount of new tokens to the connected wallet
   * @param amount - The amount of tokens to mint
   * @returns the transaction result of the mint
   *
   * @example
   * ```jsx
   * // Specify the amount of tokens to mint
   * const amount = 1;
   * // And then you can mint the tokens
   * const tx = await program.mint(amount);
   * ```
   */
  async mint(amount) {
    return this.mintTo(this.metaplex.identity().publicKey.toBase58(), amount);
  }

  /**
   * Mints the specified amount of new tokens to a specific wallet
   * @param amount - The amount of tokens to mint
   * @returns the transaction result of the mint
   *
   * @example
   * ```jsx
   * // Specify the address to mint tokens to
   * const address = "{{wallet_address}}"";
   * // And the amount of tokens to mint
   * const amount = 1;
   * // And then you can make a mint transaction
   * const tx = await program.mintTo(address, 1);
   * ```
   */
  async mintTo(receiverAddress, amount) {
    const amountParsed = AmountSchema.parse(amount);
    const info = await this.getMint();
    const result = await this.metaplex.tokens().mint({
      amount: token(amountParsed, info.decimals),
      mintAddress: this.publicKey,
      toOwner: new PublicKey(receiverAddress)
    });
    return {
      signature: result.response.signature
    };
  }

  /**
   * Transfer the specified amount of tokens to another wallet
   * @param receiverAddress - The address to send the tokens to
   * @param amount - The amount of tokens to send
   * @returns the transaction result of the transfer
   *
   * @example
   * ```jsx
   * // Specify the address to transfer tokens to
   * const to = "...";
   * // And the amount of tokens to transfer
   * const amount = 1;
   * // And then you can make the transfer transaction
   * const tx = await program.transfer(to, amount);
   * ```
   */
  async transfer(receiverAddress, amount) {
    const info = await this.getMint();
    const result = await this.metaplex.tokens().send({
      mintAddress: this.publicKey,
      amount: token(amount, info.decimals),
      toOwner: new PublicKey(receiverAddress)
    });
    return {
      signature: result.response.signature
    };
  }

  /**
   * Get the token balance of the connected wallet
   * @returns the currency value balance
   *
   * @example
   * ```jsx
   * const balance = await program.balance();
   * console.log(balance.displayValue);
   * ```
   */
  async balance() {
    return this.balanceOf(this.metaplex.identity().publicKey.toBase58());
  }

  /**
   * Get the token balance of the specified wallet
   * @param walletAddress - the wallet address to get the balance of
   * @returns the currency value balance
   *
   * @example
   * ```jsx
   * const address = "..."
   * const balance = await program.balanceOf(address);
   * console.log(balance.displayValue);
   * ```
   */
  async balanceOf(walletAddress) {
    const mint = await this.getMint();
    const addr = await getAssociatedTokenAddress(this.publicKey, new PublicKey(walletAddress));
    try {
      const account = await getAccount(this.connection, addr);
      return toCurrencyValue({
        basisPoints: toBigNumber(account.amount.toString()),
        currency: {
          symbol: "",
          decimals: mint.decimals,
          namespace: "spl-token"
        }
      });
    } catch (e) {
      throw Error(`No balance found for address '${walletAddress}' - ${e}`);
    }
  }
  async getMint() {
    return await this.metaplex.tokens().findMintByAddress({
      address: this.publicKey
    });
  }
}

var TWRegistryIDL = {
	version: "0.1.0",
	name: "tw_registry",
	instructions: [
		{
			name: "initializeRegistrar",
			accounts: [
				{
					name: "registrarAccount",
					isMut: true,
					isSigner: false
				},
				{
					name: "authority",
					isMut: true,
					isSigner: true
				},
				{
					name: "systemProgram",
					isMut: false,
					isSigner: false
				}
			],
			args: [
			]
		},
		{
			name: "register",
			accounts: [
				{
					name: "registrarAccount",
					isMut: true,
					isSigner: false
				},
				{
					name: "registeredProgramAccount",
					isMut: true,
					isSigner: false
				},
				{
					name: "authority",
					isMut: true,
					isSigner: true
				},
				{
					name: "systemProgram",
					isMut: false,
					isSigner: false
				}
			],
			args: [
				{
					name: "programAddress",
					type: "publicKey"
				},
				{
					name: "programName",
					type: "string"
				},
				{
					name: "programType",
					type: "string"
				}
			]
		},
		{
			name: "setVisibility",
			accounts: [
				{
					name: "registeredProgramAccount",
					isMut: true,
					isSigner: false
				},
				{
					name: "authority",
					isMut: false,
					isSigner: true
				}
			],
			args: [
				{
					name: "programIndex",
					type: "u64"
				},
				{
					name: "visible",
					type: "bool"
				}
			]
		}
	],
	accounts: [
		{
			name: "RegistrarAccount",
			type: {
				kind: "struct",
				fields: [
					{
						name: "count",
						type: "u64"
					},
					{
						name: "authority",
						type: "publicKey"
					},
					{
						name: "bump",
						type: "u8"
					}
				]
			}
		},
		{
			name: "RegisteredProgramAccount",
			type: {
				kind: "struct",
				fields: [
					{
						name: "authority",
						type: "publicKey"
					},
					{
						name: "programAddress",
						type: "publicKey"
					},
					{
						name: "programName",
						type: "string"
					},
					{
						name: "programType",
						type: "string"
					},
					{
						name: "visible",
						type: "bool"
					},
					{
						name: "bump",
						type: "u8"
					}
				]
			}
		}
	],
	events: [
		{
			name: "RegistrarInitializedEvent",
			fields: [
				{
					name: "authority",
					type: "publicKey",
					index: false
				}
			]
		},
		{
			name: "ProgramRegisteredEvent",
			fields: [
				{
					name: "authority",
					type: "publicKey",
					index: false
				},
				{
					name: "programAddress",
					type: "publicKey",
					index: false
				},
				{
					name: "programName",
					type: "string",
					index: false
				},
				{
					name: "programType",
					type: "string",
					index: false
				}
			]
		}
	],
	metadata: {
		address: "twregzGdRmyFeAKjPgbPMkRkzgFNy8BHrB4HzwjyH14"
	}
};

/**
 * @internal
 */
class Registry {
  constructor(metaplex, userWallet) {
    _defineProperty(this, "metaplex", void 0);
    _defineProperty(this, "twRegistry", void 0);
    this.metaplex = metaplex;
    this.twRegistry = new Program(TWREGISTRY_PROGRAM_ID, TWRegistryIDL, this.metaplex.connection, userWallet);
  }
  async getProgramType(address) {
    try {
      const candyMachine = await this.metaplex.candyMachinesV2().findByAddress({
        address: new PublicKey(address)
      });
      if (candyMachine) {
        return "nft-drop";
      }
    } catch (err) {
      // ignore and try next
    }
    const metadata = await this.metaplex.nfts().findByMint({
      mintAddress: new PublicKey(address)
    });
    if (metadata) {
      if (metadata.collectionDetails) {
        return "nft-collection";
      } else {
        if (metadata.tokenStandard === TokenStandard.Fungible) {
          return "token";
        }
      }
    }
    throw new Error("Unknown account type");
  }
  async getDeployedPrograms(walletAddress) {
    const wallet = new PublicKey(walletAddress);
    const count = await this.getTotalProgramsRegistered(wallet);
    const programAddresses = [];
    for (let i = 0; i < count; i++) {
      programAddresses.push(this.getRegisteredProgramAddress(wallet, i).toBase58());
    }
    const programsRaw = await this.twRegistry.fetchMultiple("registeredProgramAccount", programAddresses);
    return programsRaw.map(raw => this.toRegisteredProgram(raw));
  }
  async getTotalProgramsRegistered(wallet) {
    const accountExists = await this.registrarAccountExists(wallet);
    if (!accountExists) {
      return 0;
    }
    const registrarPda = this.getRegistrarAddress(wallet);
    const data = await this.twRegistry.fetch("registrarAccount", registrarPda.toBase58());
    return data.count.toNumber();
  }
  async getProgramAt(walletAddress, index) {
    const wallet = new PublicKey(walletAddress);
    const pda = this.getRegisteredProgramAddress(wallet, index);
    const data = await this.twRegistry.fetch("registeredProgramAccount", pda.toBase58());
    return this.toRegisteredProgram(data);
  }
  getRegistrarAddress(wallet) {
    return Pda.find(new PublicKey(TWREGISTRY_PROGRAM_ID), [Buffer$1.from("registrar", "utf8"), wallet.toBuffer()]);
  }
  getRegisteredProgramAddress(wallet, index) {
    return Pda.find(new PublicKey(TWREGISTRY_PROGRAM_ID), [Buffer$1.from("registered_program"), wallet.toBuffer(), Buffer$1.from(index.toString())]);
  }

  /**
   * @internal
   */
  async getAddToRegistryInstructions(programToAdd, programName, programType) {
    const wallet = this.metaplex.identity().publicKey;
    const instructions = [];
    const registrarAccountExists = await this.registrarAccountExists(wallet);
    if (!registrarAccountExists) {
      instructions.push({
        instruction: (await this.getInitializeRegistrarTransaction(wallet)).instructions[0],
        signers: [this.metaplex.identity()]
      });
    }
    instructions.push({
      instruction: (await this.getRegisterProgramTransaction(wallet, programToAdd, programName, programType)).instructions[0],
      signers: [this.metaplex.identity()]
    });
    return instructions;
  }
  async getInitializeRegistrarTransaction(wallet) {
    const registrarPda = this.getRegistrarAddress(wallet);
    return this.twRegistry.prepareCall("initializeRegistrar", {
      accounts: {
        authority: wallet.toBase58(),
        registrarAccount: registrarPda.toBase58()
      }
    }).transaction();
  }
  async getRegisterProgramTransaction(wallet, programAddress, programName, programType) {
    const registrarPda = this.getRegistrarAddress(wallet);
    const registeredProgramAddress = this.getRegisteredProgramAddress(wallet, await this.getTotalProgramsRegistered(wallet));
    return this.twRegistry.prepareCall("register", {
      accounts: {
        authority: wallet.toBase58(),
        registrarAccount: registrarPda.toBase58(),
        registeredProgramAccount: registeredProgramAddress.toBase58()
      },
      data: [programAddress, programName, programType]
    }).transaction();
  }
  async registrarAccountExists(wallet) {
    const registrarPda = this.getRegistrarAddress(wallet);
    const account = await this.metaplex.rpc().getAccount(registrarPda);
    return account.exists;
  }
  getDropForCollection(candyMachines, meta) {
    return candyMachines.find(candyMachine => candyMachine.collectionMintAddress?.toBase58() === meta.mintAddress.toBase58());
  }

  // TODO probably don't need this anymore, rely on registry instead
  async getOwnedTokenAccounts(walletAddress) {
    const mints = await this.getOwnedTokenAddreses(walletAddress);
    const metadatas = await this.metaplex.nfts().findAllByMintList({
      mints
    });
    const candyMachines = await this.metaplex.candyMachinesV2().findAllBy({
      type: "authority",
      publicKey: new PublicKey(walletAddress)
    });
    return metadatas.map(mintMetadata => {
      const meta = mintMetadata;
      if (!meta) {
        return undefined;
      }
      if (meta?.collectionDetails) {
        // check if it's part of a candy machine
        const drop = this.getDropForCollection(candyMachines, meta);
        if (drop) {
          return {
            type: "nft-drop",
            address: drop.address.toBase58(),
            name: meta.name
          };
        } else {
          return {
            type: "nft-collection",
            address: meta.mintAddress.toBase58(),
            name: meta.name
          };
        }
      } else {
        if (meta.tokenStandard === TokenStandard.Fungible) {
          return {
            type: "token",
            address: meta.mintAddress.toBase58(),
            name: meta.name
          };
        }
      }
    }).filter(account => account !== undefined);
  }
  async getOwnedTokenAddreses(walletAddress) {
    return await new TokenGpaBuilder(this.metaplex).selectMint().whereOwner(new PublicKey(walletAddress)).getDataAsPublicKeys();
  }
  toRegisteredProgram(data) {
    return {
      deployer: data.authority.toBase58(),
      programAddress: data.programAddress.toBase58(),
      programName: data.programName.toString(),
      programType: data.programType.toString(),
      visible: data.visible
    };
  }
}

/**
 * The main entry-point for the thirdweb Solana SDK.
 *
 * @example
 * ```jsx
 * import { ThirdwebSDK } from "@thirdweb-dev/sdk/solana";
 *
 * // Create SDK on specified network, and then pass a signer
 * const sdk = ThirdwebSDK.fromNetwork("devnet");
 * // Signer can be a keypair or browser wallet adapter
 * sdk.wallet.connect(signer);
 * ```
 *
 * @public
 */
class ThirdwebSDK {
  /**
   * Create a new SDK instance for the specified network
   * @param network - The network to connect to
   * @param storage - The storage provider to use or IPFS by default
   * @returns an SDK instance
   */
  static fromNetwork(network, storage) {
    return new ThirdwebSDK(new Connection(getUrlForNetwork(network), {
      disableRetryOnRateLimit: true,
      commitment: "confirmed"
    }), storage);
  }

  /**
   * reate a new SDK instance connected with the given private key
   * @param network - The network to connect to
   * @param privateKey - The private key to use
   * @param storage - The storage provider to use or IPFS by default
   * @returns an SDK instance
   */
  static fromPrivateKey(network, privateKey, storage) {
    const sdk = ThirdwebSDK.fromNetwork(network, storage);
    const keypair = Keypair.fromSecretKey(bs58.decode(privateKey));
    sdk.wallet.connect(keypair);
    return sdk;
  }
  /**
   * The currently connected network
   */
  get network() {
    const url = new URL(this.connection.rpcEndpoint);
    // try this first to avoid hitting `custom` network for alchemy urls
    if (url.hostname.includes("devnet")) {
      return "devnet";
    }
    if (url.hostname.includes("mainnet")) {
      return "mainnet-beta";
    }
    return this.metaplex.cluster;
  }
  constructor(connection) {
    let storage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new ThirdwebStorage({
      uploader: new IpfsUploader({
        uploadWithGatewayUrl: true
      })
    });
    _defineProperty(this, "connection", void 0);
    _defineProperty(this, "metaplex", void 0);
    _defineProperty(this, "storage", void 0);
    _defineProperty(this, "registry", void 0);
    _defineProperty(this, "deployer", void 0);
    _defineProperty(this, "wallet", void 0);
    _defineProperty(this, "auth", void 0);
    this.connection = connection;
    this.storage = storage;
    this.metaplex = Metaplex.make(this.connection);
    this.wallet = new UserWallet(this.metaplex);
    this.auth = new WalletAuthenticator(this.wallet);
    this.registry = new Registry(this.metaplex, this.wallet);
    this.deployer = new Deployer(this.registry, this.metaplex, this.storage);
  }

  /**
   * Get an SDK interface for an NFT Collection program
   * @param address - Address of the program
   * @returns SDK interface for the program
   */
  async getNFTCollection(address) {
    return this.getProgram(address, "nft-collection");
  }

  /**
   * Get an SDK interface for an NFT Drop program
   * @param address - Address of the program
   * @returns SDK interface for the program
   */
  async getNFTDrop(address) {
    return this.getProgram(address, "nft-drop");
  }

  /**
   * Get an SDK interface for an Token program
   * @param address - Address of the program
   * @returns SDK interface for the program
   */
  async getToken(address) {
    return this.getProgram(address, "token");
  }

  /**
   * Get an SDK interface for a deployed program
   * @param address - Address of the program
   * @returns SDK interface for the program
   *
   * @example
   * ```jsx
   * // Get the interface for your anchor program
   * const program = await sdk.getProgram("{{program_address}}");
   * ```
   */

  async getProgram(address, programTypeOrIdl) {
    // if we have a programType or IDL
    if (programTypeOrIdl) {
      // if it's a prebuilt program type
      if (typeof programTypeOrIdl === "string") {
        switch (programTypeOrIdl) {
          case "nft-collection":
            return new NFTCollection(address, this.metaplex, this.storage);
          case "nft-drop":
            return new NFTDrop(address, this.metaplex, this.storage);
          case "token":
            return new Token(address, this.metaplex, this.storage);
          default:
            throw new Error("Invalid program type");
        }
      }
      // otherwise, it's an IDL, so return a program with that IDL
      return this.getProgramWithIdl(address, programTypeOrIdl);
    }
    const anchor = await import('@project-serum/anchor');
    const idl = await anchor.Program.fetchIdl(address, new anchor.AnchorProvider(this.connection, this.metaplex.identity(), {}));
    if (!idl) {
      throw new Error(`Could not fetch IDL for program at address '${address}'`);
    }
    return this.getProgramWithIdl(address, idl);
  }

  /**
   * Get an SDK interface for a deployed program
   * @param address - Address of the program
   * @param idl - The IDL of the program
   * @returns SDK interface for the program
   *
   * @example
   * ```jsx
   * import idl from "path/to/idl.json"
   *
   * // Alternatively, you can pass in your own IDL
   * const program = await sdk.getProgramWithIdl(address, idl);
   * ```
   */
  async getProgramWithIdl(address, idl) {
    const program$1 = await Promise.resolve().then(function () { return program; });
    return new program$1.Program(address, idl, this.connection, this.wallet);
  }
}

export { AddressSchema as A, CommonContractSchema as C, Deployer as D, NFTCollectionMetadataInputSchema as N, OptionalPropertiesInput as O, Program as P, RawDateSchema as R, TokenMetadataInputSchema as T, UserWallet as U, WalletAuthenticator as W, NFTDropContractInputSchema as a, NFTCollection as b, NFTDrop as c, Token as d, ThirdwebSDK as e, CommonContractOutputSchema as f, CreatorInputSchema as g, NFTDropInitialConditionsInputSchema as h, NFTDropUpdateableConditionsInputSchema as i, NFTDropUpdateableConditionsOutputSchema as j, getUrlForNetwork as k, getNework as l, getPublicRpc as m };
